import { z } from "zod";

export const proposalSteps = [
	{
		key: "personal",
		label: "Dados pessoais",
		title: "Vamos comecar pelos seus dados",
		description:
			"Precisamos de algumas informacoes para iniciar sua solicitacao.",
		fields: ["name", "email", "phone"] as const,
	},
	{
		key: "company",
		label: "Dados da empresa",
		title: "Agora fale um pouco sobre a empresa",
		description:
			"Essas informacoes ajudam nosso time a contextualizar melhor o projeto.",
		fields: ["company", "colaborators"] as const,
	},
	{
		key: "type",
		label: "Tipo",
		title: "Qual e o tipo do seu projeto?",
		description:
			"Escolha a opcao que mais se aproxima do que voce quer construir.",
		fields: ["types"] as const,
	},
	{
		key: "budget",
		label: "Orçamento",
		title: "Qual o orçamento disponível?",
		description:
			"Isso nos ajuda a enquadrar a proposta no nível de complexidade esperado.",
		fields: ["budget"] as const,
	},
	{
		key: "details",
		label: "Detalhes",
		title: "Conta pra gente os detalhes do seu projeto",
		description:
			"Quanto mais contexto voce trouxer, melhor conseguimos preparar o proximo passo.",
		fields: ["note"] as const,
	},
] as const;

export const collaboratorOptions = [
	"0 - 10",
	"11 - 30",
	"Acima de 30",
] as const;

export const projectTypeOptions = ["Site", "Sistema", "Aplicativo"] as const;

export const budgetsByType = {
	Site: [
		"R$ 8 mil - R$ 15 mil",
		"R$ 15 mil - R$ 30 mil",
		"R$ 30 mil - R$ 60 mil",
		"R$ 60 mil +",
	],
	Sistema: [
		"R$ 30 mil - R$ 60 mil",
		"R$ 60 mil - R$ 100 mil",
		"R$ 100 mil - R$ 150 mil",
		"R$ 150 mil +",
	],
	Aplicativo: [
		"R$ 40 mil - R$ 70 mil",
		"R$ 70 mil - R$ 100 mil",
		"R$ 100 mil - R$ 150 mil",
		"R$ 150 mil +",
	],
} as const satisfies Record<
	(typeof projectTypeOptions)[number],
	readonly string[]
>;

export const proposalFormSchema = z.object({
	name: z.string().trim().min(3, "Por favor, digite seu nome completo."),
	email: z.string().trim().email("Por favor, digite um e-mail valido."),
	phone: z
		.string()
		.trim()
		.regex(
			/^\(\d{2}\) \d{5}-\d{4}$/,
			"Por favor, digite um telefone valido no formato (99) 99999-9999.",
		),
	company: z.string().trim().min(3, "Por favor, digite o nome da empresa."),
	colaborators: z
		.array(z.string())
		.min(1, "Por favor, selecione a quantidade de colaboradores."),
	types: z.array(z.string()).min(1, "Por favor, selecione o tipo do projeto."),
	budget: z
		.string()
		.trim()
		.min(1, "Por favor, selecione o orçamento disponível."),
	note: z
		.string()
		.trim()
		.min(3, "Por favor, conte um pouco mais sobre o projeto."),
	origin: z.string().trim().optional(),
	utm_source: z.string().trim().optional(),
	utm_medium: z.string().trim().optional(),
	utm_campaign: z.string().trim().optional(),
	utm_content: z.string().trim().optional(),
	utm_term: z.string().trim().optional(),
});

export type ProposalFormValues = z.infer<typeof proposalFormSchema>;

/** Origem do lead quando a sessão não guardou a página que trouxe o visitante. */
export const proposalDefaultOriginLabel = "Site Flowlab";

export function getBudgetOptionsForType(
	type?: string | null,
): readonly string[] {
	if (!type) {
		return [];
	}

	return budgetsByType[type as keyof typeof budgetsByType] ?? [];
}

export function formatPhone(value: string) {
	const digits = value.replace(/\D/g, "").slice(0, 11);

	if (digits.length <= 2) {
		return digits ? `(${digits}` : "";
	}

	if (digits.length <= 7) {
		return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
	}

	return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}
